import Alpine from '../app/entry-setup/alpine-setup';
import { WafInterruptionError } from '../app/api';

import i18n from 'app/i18n';
import { verificationRecaptcha } from 'app/mixins/verification-recaptcha';
import _, { findIndex, reverse } from 'lodash';
import '../stylesheets/application.scss';

function initialPage(testing) {
  if (testing) {
    return 'testing'
  } else {
    return 'overview'
  }
}

const modalPages = [
  'secondary-login-method',
  'password',
  'personal-info',
  'profile-photo',
  'verify-programs',
]

function OSUController(data) {

  return {
    t: {
      char_max: i18n.t('open_sign_up.secondary_program.character_max'),
    },
    page: data.sessionPage || initialPage(window.FeatureFlag['scholars_testing_password_on?']),
    showContinuationModal: data.sessionPage && modalPages.includes(data.sessionPage),
    allPrograms: data.allPrograms,
    eSheSchoolId: data.eSheSchoolId,
    user_registration: data.user_registration ||
    {
      mcfStaff: false,
      userPrograms: [],

      acceptedAt: {
        tosAcceptedAt: null,
        notificationPolicyAcceptedAt: null
      },

      userPrimaryLogin: {
        loginMethod: null,
        countryCode: '_none',
        verificationStatus: null,
      },
      userSecondaryLogin: {
        loginMethod: '',
        countryCode: '_none',
      },

      password: '',
      passwordConfirmed: false,

      userInfo: {
        interest: null,
        age: '_none',
        firstName: '',
        lastName: '',
        livesIn: [],
        originallyFrom: [],
        gender: '_none',
      },

      avatarData: {
        profile_image: null,
        profile_attributes: {
          original_profile_image: null,
        },
      },
      userId: null,
    },

    tosAccepted: false,
    notificationPolicyAccepted: false,

    errors: {},
    isSaving: false,
    validationHeading: i18n.t('open_sign_up.validation.unable_to_proceed'),

    clearErrors() {
      this.errors = {};
    },

    scrollToError() {
      this.$nextTick(() => {
        const errorEl = document.getElementById('open-sign-up-error-alert');
        if (errorEl) {
          errorEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
          errorEl.focus();
        }
      });
    },

    validateOverview() {
      this.clearErrors();
      if (!this.user_registration.userInfo.interest) {
        this.errors.interest = i18n.t('open_sign_up.validation.select_interest');
      }
      if (!this.user_registration.userInfo.age || this.user_registration.userInfo.age === '_none') {
        this.errors.age = i18n.t('open_sign_up.validation.select_age');
      }
      if (Object.keys(this.errors).length > 0) {
        this.scrollToError();
        return false;
      }
      return true;
    },

    validateTermsConditions() {
      this.clearErrors();
      if (this.user_registration.acceptedAt.tosAcceptedAt == null) {
        this.errors.tos = i18n.t('open_sign_up.validation.accept_terms');
      }
      if (Object.keys(this.errors).length > 0) {
        this.scrollToError();
        return false;
      }
      return true;
    },

    validatePrimaryPrograms() {
      this.clearErrors();
      if (this.user_registration.userPrograms.length < 1) {
        this.errors.program = i18n.t('open_sign_up.validation.select_program');
      }
      if (Object.keys(this.errors).length > 0) {
        this.scrollToError();
        return false;
      }
      return true;
    },

    validateSecondaryPrograms(otherText) {
      this.clearErrors();
      const incomplete = this.user_registration.userPrograms.filter((prog) => {
        if (prog.primary.name != 'Other') {
          return prog.secondaries.length < 1;
        } else {
          return prog.primary.complete == false && (prog.secondaries[0].length == 0 || (prog.secondaries[0] && prog.secondaries[0].secondary_name == 'Other' && !(otherText && otherText.length > 0)));
        }
      });
      if (incomplete.length > 0) {
        const hasOtherMissing = incomplete.some(prog => prog.primary.name === 'Other' && prog.secondaries[0] && prog.secondaries[0].secondary_name === 'Other');
        if (hasOtherMissing) {
          this.errors.otherText = i18n.t('open_sign_up.validation.provide_other_text');
        } else {
          this.errors.secondary = i18n.t('open_sign_up.validation.select_secondary_program');
        }
      }
      if (this.user_registration.userPrograms.length == 0) {
        this.errors.program = i18n.t('open_sign_up.validation.select_program');
      }
      if (Object.keys(this.errors).length > 0) {
        this.scrollToError();
        return false;
      }
      return true;
    },

    validateSchoolSelection(checked) {
      this.clearErrors();
      if (!checked || checked.length == 0) {
        this.errors.school = i18n.t('open_sign_up.validation.select_school');
      }
      if (Object.keys(this.errors).length > 0) {
        this.scrollToError();
        return false;
      }
      return true;
    },

    validateScholarStatus(schoolPrograms) {
      this.clearErrors();
      if (this.isScholarSelectionButtonDisabled(schoolPrograms)) {
        this.errors.scholar = i18n.t('open_sign_up.validation.select_scholar_status');
      }
      if (Object.keys(this.errors).length > 0) {
        this.scrollToError();
        return false;
      }
      return true;
    },

    validatePassword(disableSubmitFn) {
      this.clearErrors();
      const password = this.user_registration.password;
      const passwordTwo = document.getElementById('reenter-password')?.value || '';

      if (!password) {
        this.errors.password = i18n.t('open_sign_up.validation.enter_password');
      } else if (password.length < 15) {
        this.errors.password = i18n.t('open_sign_up.validation.password_too_short');
      } else if (passwordTwo !== password) {
        this.errors.password = i18n.t('open_sign_up.validation.passwords_do_not_match');
      } else if (disableSubmitFn()) {
        this.errors.password = i18n.t('open_sign_up.validation.confirm_password');
      }
      if (Object.keys(this.errors).length > 0) {
        this.scrollToError();
        return false;
      }
      return true;
    },

    validatePersonalInfo() {
      this.clearErrors();
      if (!this.user_registration.userInfo.firstName && !this.user_registration.userInfo.lastName) {
        this.errors.name = i18n.t('open_sign_up.validation.enter_first_or_last_name');
      }
      if (this.user_registration.userInfo.livesIn.length <= 0) {
        this.errors.livesIn = i18n.t('open_sign_up.validation.select_lives_in');
      }
      if (this.user_registration.userInfo.originallyFrom.length <= 0) {
        this.errors.originallyFrom = i18n.t('open_sign_up.validation.select_originally_from');
      }
      if (this.user_registration.userInfo.gender === '_none') {
        this.errors.gender = i18n.t('open_sign_up.validation.select_gender');
      }
      if (Object.keys(this.errors).length > 0) {
        this.scrollToError();
        return false;
      }
      return true;
    },

    validateVerifyPrograms(sortedPrograms, otherText) {
      this.clearErrors();
      if (this.user_registration.userPrograms.some(prog => prog.primary.name === 'Other') && sortedPrograms[sortedPrograms.length - 1].secondary_name === 'Other' && (!otherText || otherText.length === 0)) {
        this.errors.otherText = i18n.t('open_sign_up.validation.provide_verify_other_text');
      }
      if (Object.keys(this.errors).length > 0) {
        this.scrollToError();
        return false;
      }
      return true;
    },

    getStep(page) {
      switch (page) {
        case 'terms-conditions':
        case 'primary-login-method':
          return 1;
        case 'verify-login':
          return 1.5;
        case 'very-your-login':
          return 2;
        case 'password':
          return 3;
        case 'secondary-login-method':
          return 4;
        case 'personal-info':
          return 5;
        case 'primary-selection':
          return 6;
        case 'secondary-selection':
          return 7;
        case 'school-selection':
          return 8;
        case 'scholar-status-selection':
          return 9;
        case 'verify-programs':
          return 9.6;
        default:
          return 0;
      }
    }, // getStep used by both progress-indicator and step-bar partials
    getScholarStatusSelectionTitle(locale) {
      let allProgramsWithSchools = this.user_registration.userPrograms.filter((prog) => {
        const mappedSecondaries = prog.secondaries.filter(sec => (sec.include_schools));
        if (mappedSecondaries.length > 0) {
          return true;
        }
        return false;
      });
      const title = locale ? allProgramsWithSchools[0].primary.name : allProgramsWithSchools[0].primary.name_fr || allProgramsWithSchools[0].primary.name;
      return title;
    },
    validateCodeAndClickNext() {
      if (this.user_registration.userInfo.age < 18 || this.user_registration.userInfo.interest === 'no') {
        this.goToNextPage('ineligible');
      } else if (this.mcfStaffCode != '') {
        let progData = {}
        this.user_registration.mcfStaff = true;
        switch (this.mcfStaffCode.trim().toLowerCase()) {
          case 'mastercardfdn.org':
            progData['primary'] = this.allPrograms.find((prog) => prog.primaryProgram.name == 'Other').primaryProgram
            progData['secondary'] = this.allPrograms.find((prog) => prog.primaryProgram.name == 'Other').secondaryPrograms.find((sec) => sec.name == 'I am Mastercard Foundation staff')
            break;
          case 'ggbcmentor':
            progData['primary'] = this.allPrograms.find((prog) => prog.primaryProgram.name == 'Other').primaryProgram
            progData['secondary'] = this.allPrograms.find((prog) => prog.primaryProgram.name == 'Other').secondaryPrograms.find((sec) => sec.name == 'I am a mentor')
            break;
          case 'e-she':
            progData['primary'] = this.allPrograms.find((prog) => prog.primaryProgram.name == 'Mastercard Foundation Scholars Program').primaryProgram
            progData['secondary'] = this.allPrograms.find((prog) => prog.primaryProgram.name == 'Mastercard Foundation Scholars Program').secondaryPrograms.find((sec) => sec.name == 'Scholar')
            progData['secondary']['school_ids'] = [this.eSheSchoolId]
            break;
          default:
            this.user_registration.mcfStaff = false;
            this.codeError = true;
        }

        if (this.user_registration.mcfStaff == true) {
          const codeProgram = [
            {
              primary_id: progData.primary.id,
              primary_name: progData.primary.name,
              primary_name_fr: progData.primary.name_fr,
              secondaries: [
                {
                  secondary_id: progData.secondary.id,
                  secondary_name: progData.secondary.name,
                  secondary_name_fr: progData.secondary.name_fr,
                  school_ids: progData.secondary.school_ids
                },
              ],
            },
          ];
          this.user_registration.userPrograms = codeProgram;
          this.goToNextPage('terms-conditions');
        }
      } else {
        this.user_registration.mcfStaff = false;
        this.goToNextPage('terms-conditions');
      }
    },

    processPrimaryPrograms() {
      this.user_registration.userPrograms.map((program) => {
        const completeProgram = this.allPrograms.find((prog) => { return prog.primaryProgram.id == program.primary.id })
        const secondary = completeProgram.secondaryPrograms[0]
        if (completeProgram.secondaryPrograms.length == 1 && secondary.include_schools == false) {
          program.primary.complete = true
          program.primary.completed_at = Date.now()
          if (!program.secondaries.find((sec) => { return sec.secondary_id == secondary.id })) {
            program.secondaries.push({ complete: true, other_text: null, secondary_name: secondary.display_name, secondary_name_fr: secondary.display_name_fr, secondary_id: secondary.id, include_schools: secondary.include_schools, schools_completed_at: null, school_ids: [], scholar_selection_complete: false, school_selected_secondary_ids: {} })
          }
        }
        else if (completeProgram.secondaryPrograms.length == 1) {
          program.primary.complete = true
          program.primary.completed_at = Date.now()
          if (!program.secondaries.find((sec) => { return sec.secondary_id == secondary.id })) {
            program.secondaries.push({ complete: false, other_text: null, secondary_name: secondary.display_name, secondary_name_fr: secondary.display_name_fr, secondary_id: secondary.id, include_schools: secondary.include_schools, schools_completed_at: null, school_ids: [], scholar_selection_complete: false, school_selected_secondary_ids: {} })
          }
        }
        else if (completeProgram.secondaryPrograms.length > 1 && secondary.include_schools == true) {
          // captures scholars program and any other primary that includes schools and has multiple secondaries
          // mark them as complete so they don't appear on the secondary selection screen
          program.primary.complete = true
          program.primary.completed_at = Date.now()
          completeProgram.secondaryPrograms.map((secondaryProg) => {
            if (!program.secondaries.find((sec) => { return sec.secondary_id == secondaryProg.id })) {
              program.secondaries.push({ complete: false, other_text: null, secondary_name: secondaryProg.display_name, secondary_name_fr: secondaryProg.display_name_fr, secondary_id: secondaryProg.id, include_schools: secondaryProg.include_schools, schools_completed_at: null, school_ids: [], scholar_selection_complete: false, school_selected_secondary_ids: {} })
            }
          })
        }
        return program
      })
      this.goToNextPage(this.programNextPage())
    },
    processSecondaryPrograms() {
      const programs = this.user_registration.userPrograms.filter((prog) => { return prog.primary.complete == false && prog.secondaries.length != 0 }) // This should gather all secondaries handled on the page
      programs.map((program) => {
        program.secondaries.map((sec) => {
          if (!sec.include_schools) {
            sec.complete = true
          }
          program.primary.complete = true
          program.primary.completed_at = Date.now()
        })
      })
      this.goToNextPage(this.programNextPage())
    },
    processScholarSelection(schoolPrograms) {
      const compiledSchoolIds = {};
      // compile arrays of school ids for appropriate primary and secondary programs
      schoolPrograms.forEach((prog) => {
        // if entries don't exist already add them to prevent undefined errors
        if (compiledSchoolIds[prog.primary_id] === undefined) {
          compiledSchoolIds[prog.primary_id] = {};
        }
        if (compiledSchoolIds[prog.primary_id][prog.selected_secondary] === undefined) {
          compiledSchoolIds[prog.primary_id][prog.selected_secondary] = [];
        }
        compiledSchoolIds[prog.primary_id][prog.selected_secondary].push(prog.school_id);
      });

      // map the compiled school ids to the appropriate userPrograms
      this.user_registration.userPrograms.map((prog) => {
        // if program's primary Id is one of the primaries in compiledSchoolIds set the appropriate secondary's school_ids
        if (compiledSchoolIds[prog.primary.id] !== undefined) {
          return prog.secondaries.map((secondaryProg) => {
            // if compiledSchoolIds is empty save an empty array instead of undefined
            const schoolIds = compiledSchoolIds[prog.primary.id][secondaryProg.secondary_id] || [];
            secondaryProg.school_ids = schoolIds;
            return secondaryProg;
          });
        }
        else {
          return prog;
        }
      });
      // remove any secondaries that have no school selections
      this.user_registration.userPrograms.forEach((prog) => {
        // only want to process programs that have school data to clean up
        if (compiledSchoolIds[prog.primary.id] !== undefined) {
          prog.secondaries = prog.secondaries.filter(secondaryProg => secondaryProg.school_ids.length !== 0);
        }
      });
    },
    saveScholarSelections(schoolPrograms) {
      const compiledSchoolIdsWithSelectedSecondary = {};
      // compile arrays of school ids for appropriate primary and secondary programs
      schoolPrograms.forEach((prog) => {
        // if entries don't exist already add them to prevent undefined errors
        if (compiledSchoolIdsWithSelectedSecondary[prog.primary_id] === undefined) {
          compiledSchoolIdsWithSelectedSecondary[prog.primary_id] = {};
        }
        compiledSchoolIdsWithSelectedSecondary[prog.primary_id][prog.school_id] = parseInt(prog.selected_secondary, 10);
      });
      // save the object into the user data
      this.user_registration.userPrograms.map((prog) => {
        if (compiledSchoolIdsWithSelectedSecondary[prog.primary.id] !== undefined) {
          // found primary program to save data to, insert the data
          return prog.secondaries.map((secondaryProg) => {
            secondaryProg.school_ids.forEach((schoolId) => {
              if (compiledSchoolIdsWithSelectedSecondary[prog.primary.id][schoolId] !== undefined) {
                const selectedSecondary = compiledSchoolIdsWithSelectedSecondary[prog.primary.id][schoolId];
                prog.secondaries[0].school_selected_secondary_ids[schoolId] = selectedSecondary;
              }
            });
            secondaryProg.scholar_selection_complete = true;
          });
        }
        return prog;
      });
    },
    processVerifyPrograms(programs, englishLocale) {
      const otherIndex = this.user_registration.userPrograms.findIndex(prog => prog.primary.name === 'Other');
      if (otherIndex !== -1) {
        // process new other secondary data
        const otherProgram = this.user_registration.userPrograms[otherIndex];
        const incomingOtherIndex = programs.findIndex(prog => prog.primary_name === 'Other');

        if (incomingOtherIndex !== -1) {
          const incomingOtherProgram = programs[incomingOtherIndex];
          otherProgram.secondaries[0].secondary_name = incomingOtherProgram.secondary_name;
          otherProgram.secondaries[0].secondary_name_fr = incomingOtherProgram.secondary_name_fr;
          otherProgram.secondaries[0].secondary_id = incomingOtherProgram.secondary_id;
          otherProgram.secondaries[0].other_text = incomingOtherProgram.other_text;
        }
      }
      const schoolPrograms = programs.filter(prog => prog.selected_secondary);
      if (schoolPrograms.length > 0) this.processScholarSelection(schoolPrograms);

      if (englishLocale) {
        this.createUser('en');
      } else {
        this.createUser('fr');
      }
    },
    fetchProgForSchool() {
      let program = this.user_registration.userPrograms.find((prog) => {
        const completeProgram = this.allPrograms.find((allprog) => {
          return allprog.primaryProgram.id == prog.primary.id
        })
        return prog.secondaries.find((sec, i) => !sec.complete && sec.include_schools && i === 0)
          && completeProgram.secondaryPrograms.length > 1
          && prog.primary.complete == true
      }) // this should grab the first program that has been through secondary processing and still needs schools attention
      if (!program) {
        program = this.user_registration.userPrograms.find((prog) => {
          return prog.secondaries.find((sec) => {
            return !sec.complete && sec.include_schools
          })
        })
      } // should grab single secondaries that need schools
      if (!program) {
        return
      } // if still no program, we're done with schools. return null so that the schools page doesn't try to re-render.
      const secondary = program.secondaries.find((sec) => {
        return !sec.complete && sec.include_schools
      })

      return { program: program, checked: secondary.school_ids, secondary: secondary }
    },
    programNextPage() {
      if (this.user_registration.userPrograms.some((program) => { return program.secondaries.some((sec) => { return sec.complete == false && sec.include_schools == true }) })) {
        // the schools page will sort out which ones to display
        return 'school-selection'
      } else if (this.user_registration.userPrograms.some((program) => { return program.secondaries.some((sec) => { return sec.complete == true && sec.include_schools == true && sec.scholar_selection_complete == false }) })) {
        return 'scholar-status-selection'
      }
      else if (this.user_registration.userPrograms.some((program) => { return program.primary.complete == false })) {
        return 'secondary-selection'
      }
      else {
        return 'verify-programs'
      }
    },
    deselectYoungestSchoolDate() {
      const secondaries = this.user_registration.userPrograms.map((prog) => { return prog.secondaries.filter((sec) => { return (sec.include_schools && sec.schools_completed_at && sec.complete) ? true : false }) })
      const sorted = _.sortBy(secondaries, [function (sec) { return sec.schools_completed_at }]).flat(Infinity).reverse()
      if (sorted.length > 0) {
        sorted.map((sec) => {
          sec.schools_completed_at = null
          sec.complete = false
          sec.scholar_selection_complete = false
          return sec;
        })
        return 'school-selection'
      } else {
        //no more schools, we need to undo a primary
        const completePrograms = this.user_registration.userPrograms.filter((prog) => { return prog.primary.complete }).map((prog) => { return this.allPrograms.find((program) => { return program.primaryProgram.id == prog.primary.id }) })
        //OTHER and any program that has multiple secondaries that do not include schools
        const multipleSecondaries = completePrograms.filter((prog) => { return prog.secondaryPrograms.length > 1 && prog.secondaryPrograms[0].include_schools == false })
        if (multipleSecondaries.length > 0) {
          let progsWithMultSeconds = this.user_registration.userPrograms.filter((prog) => { return multipleSecondaries.find((program) => { return program.primaryProgram.id == prog.primary.id }) })
          progsWithMultSeconds.map((prog) => {
            prog.primary.completed_at = null
            prog.primary.complete = false
          })
          return 'secondary-selection'
        } else {
          this.user_registration.userPrograms.map((prog) => {
            prog.primary.completed_at = null
            prog.primary.complete = false
          })
          return 'primary-selection'
        }
        // const sorted = _.sortBy(primaries, [function(prim) {return prim.completed_at}]).flat(Infinity).reverse()
      }
    },
    removeSchoolProgramFromUser(primaryProgId, schoolId) {
      this.user_registration.userPrograms.map((prog) => {
        if (prog.primary.id !== primaryProgId) return prog;
        return {
          primary: prog.primary,
          secondaries: prog.secondaries.map((secProg) => {
            if (!secProg.school_ids.includes(schoolId)) {
              return { ...secProg };
            }
            const updatedSchools = secProg.school_ids;
            updatedSchools.splice(updatedSchools.findIndex(id => id === schoolId), 1);
            return { ...secProg, schoolIds: updatedSchools };
          }),
        };
      });
    },
    removeSchoolProgramFromSortedPrograms(primaryProgId, schoolId, sortedSchoolPrograms) {
      return sortedSchoolPrograms.filter(prog => !(prog.primary_id === primaryProgId && prog.school_id === schoolId));
    },
    removeProgramFromSortedPrograms(primaryProgId, sortedPrograms) {
      return sortedPrograms.filter(prog => prog.primary_id !== primaryProgId);
    },
    removeProgramFromUser(primaryProgId) {
      this.user_registration.userPrograms = this.user_registration.userPrograms.filter(prog => prog.primary.id !== primaryProgId);
    },
    removeStrandedPrimaryProgramsFromUser(sortedPrograms) {
      this.user_registration.userPrograms = this.user_registration.userPrograms.filter(primaryProg => sortedPrograms.some(prog => prog.primary_id === primaryProg.primary.id));
    },
    async saveUserProfilePhoto(locale) {
      const formData = new FormData();
      const avatarData = this.user_registration.avatarData;
      const userId = this.user_registration.userId || '';
      const updatedUser = {};

      if (avatarData && avatarData.profile_attributes && avatarData.profile_attributes.original_profile_image) {
        updatedUser.profile_attributes = {
          original_profile_image: avatarData.profile_attributes.original_profile_image,
          avatar_editor_position_x: avatarData.profile_attributes.positionX,
          avatar_editor_position_y: avatarData.profile_attributes.positionY,
          avatar_editor_scale: avatarData.profile_attributes.scale,
          avatar_editor_rotate: avatarData.profile_attributes.rotate,
        };
        const avatarEditor = Alpine.store('osu').avatarEditor;
        updatedUser.profile_image = await new Promise(resolve =>
          avatarEditor.getCroppedCanvas({width: 208, height: 208})?.toBlob(resolve)
        );

        buildFormData(formData, updatedUser, 'user');
        const url = '/users/'.concat(userId);
        this.errors = {};
        this.isSaving = true;

        if (window.navigator.onLine && userId !== '') {
          this.$api({
            method: 'PATCH',
            url: url,
            data: formData,
          })
            .then((response) => {
              window.location.replace(`/users/${userId}?edit=true&locale=${locale}`);
              this.isSaving = false;
            }).catch((err) => {
              this.isSaving = false;
              this.errors.saving_error = 'There was an error saving your profile photo, please try again.';
            });
        } else {
          this.isSaving = false;
          this.errors.saving_error = 'There was an error saving your profile photo, please check your internet connection.';
        }
      } else {
        window.location.replace(`/users/${userId}?edit=true&locale=${locale}`);
      }
    },
    sortedPrimaryList(locale) {
      const allExcludeOther = this.user_registration.userPrograms.filter((prog) => { return prog.primary.name != 'Other' })
      let sorted = _.sortBy(allExcludeOther, [function (prog) { return locale ? prog.primary.name : prog.primary.name_fr }])
      let other = this.user_registration.userPrograms.find((prog) => { return prog.primary.name == 'Other' })
      if (other) {
        sorted.push(other)
      }
      return sorted
    },
    sortedSecondaryList(locale) {
      const allExcludeOther = this.user_registration.userPrograms.filter((prog) => { return prog.primary.complete == false && prog.primary.name != 'Other' })
      let sorted = _.sortBy(allExcludeOther, [function (prog) { return locale ? prog.primary.name : prog.primary.name_fr }])
      let other = this.user_registration.userPrograms.find((prog) => { return prog.primary.complete == false && prog.primary.name == 'Other' })
      if (other) {
        sorted.push(other)
      }
      return sorted
    },
    sortedPrograms(schools, locale) {
      const allPrograms = this.user_registration.userPrograms.map((prog) => {
        return prog.secondaries.map((sec) => {
          if (sec.include_schools) {
            if (sec.school_ids.length > 0) {
              return sec.school_ids.map((id) => {
                const school = schools.find((sch) => { return sch.id == id })
                return {
                  primary_name: prog.primary.name,
                  primary_name_fr: prog.primary.name_fr || prog.primary.name,
                  primary_id: prog.primary.id,
                  secondary_name: sec.secondary_name,
                  secondary_name_fr: sec.secondary_name_fr,
                  secondary_id: sec.secondary_id,
                  school_name: school.name,
                  school_name_fr: school.name_fr,
                  school_id: school.id,
                  selected_secondary: sec.school_selected_secondary_ids[school.id],
                };
              });
            }
            else {
              return { primary_name: 'emptySchoolSecondary' }
            }
          } else if (prog.primary.name == 'Other') {
            if (sec.secondary_name == 'Other') {
              return {
                primary_name: prog.primary.name,
                primary_name_fr: prog.primary.name_fr || prog.primary.name,
                primary_id: prog.primary.id,
                secondary_name: sec.secondary_name,
                secondary_name_fr: sec.secondary_name_fr,
                secondary_id: sec.secondary_id,
                other_text: sec.other_text
              }
            } else {
              return {
                primary_name: prog.primary.name,
                primary_name_fr: prog.primary.name_fr || prog.primary.name,
                primary_id: prog.primary.id,
                secondary_name: sec.secondary_name,
                secondary_name_fr: sec.secondary_name_fr,
                secondary_id: sec.secondary_id,
                other_text: "",
              }
            }
          } else {
            return {
              primary_name: prog.primary.name,
              primary_name_fr: prog.primary.name_fr || prog.primary.name,
              primary_id: prog.primary.id,
              secondary_name: sec.secondary_name,
              secondary_name_fr: sec.secondary_name_fr,
              secondary_id: sec.secondary_id,
            }
          }
        })
      }).flat(Infinity)
      const allExcludeOtherExcludeEmptySchoolSecondaries = allPrograms.filter((prog) => { return (prog.primary_name !== 'Other' && prog.primary_name !== 'emptySchoolSecondary') })
      let sorted = _.sortBy(allExcludeOtherExcludeEmptySchoolSecondaries, [function (prog) { return locale ? [prog.primary_name, prog.school_name] : [prog.primary_name_fr, prog.school_name_fr] }])
      let other = allPrograms.find((prog) => { return prog.primary_name == 'Other' })
      if (other) {
        sorted.push(other)
      }
      return sorted
    },
    sortedProgramsWithSchools(schools, locale) {
      let allProgramsWithSchools = this.user_registration.userPrograms.filter((prog) => {
        const mappedSecondaries = prog.secondaries.filter(sec => (sec.include_schools));
        if (mappedSecondaries.length > 0) {
          return true;
        }
        return false;
      });

      allProgramsWithSchools = allProgramsWithSchools.map((prog) => {
        return prog.secondaries.map((sec, index) => {
          return sec.school_ids.map((id) => {
            const school = schools.find((sch) => { return sch.id == id })
            return {
              primary_name: locale ? prog.primary.name : prog.primary.name_fr || prog.primary.name,
              primary_id: prog.primary.id,
              school_name: locale ? school.name : school.name_fr || school.name,
              school_id: school.id,
              selected_secondary: prog.secondaries[index].school_selected_secondary_ids[school.id] ? String(prog.secondaries[index].school_selected_secondary_ids[school.id]) : null,
            }
          })
        })
      }).flat(Infinity)

      const sorted = _.sortBy(allProgramsWithSchools, [function (prog) { return [prog.primary_name, prog.school_name] }]);
      return sorted;
    },
    selectionToRemove() {
      const program = this.user_registration.userPrograms.find(
        (prog) => {
          const secondary = prog.secondaries.find((sec) => { return sec.include_schools && sec.school_ids.length === 0 });
          if (secondary) {
            return true;
          }
          return false;
        },
      );
      return program;
    },
    scholarSelectionOptionsForProgram(primaryProgId, locale) {
      let associatedSecondaries = this.user_registration.userPrograms.filter(prog => prog.primary.id === primaryProgId)[0].secondaries;

      associatedSecondaries = associatedSecondaries.map(sec => ({
        id: sec.secondary_id,
        value: String(sec.secondary_id),
        name: locale ? sec.secondary_name : sec.secondary_name_fr || sec.secondary_name,
      }));
      return associatedSecondaries;
    },
    isScholarSelectionButtonDisabled(schoolPrograms) {
      return schoolPrograms.filter(prog => prog.selected_secondary === null || prog.selected_secondary === '_none').length > 0;
    },
    toggleAllIncomplete() {
      this.user_registration.userPrograms.map((prog) => {
        prog.primary.complete = false
        prog.primary.completed_at = null
        prog.secondaries.map((sec) => {
          sec.complete = false
          sec.schools_completed_at = null
          sec.scholar_selection_complete = false
        })
      })
    },
    charCount(len, maxLen) {
      const separatorChar = i18n.language === 'fr' ? '.' : ',';
      const length = len.toString().replace(/\B(?=(\d{3})+(?!\d))/g, separatorChar) // adds comma to numbers > 3 digits
      const maxLength = maxLen.toString().replace(/\B(?=(\d{3})+(?!\d))/g, separatorChar)

      return (
        `${maxLength} ${this.t.char_max}: ${length}/${maxLength}`
      );
    },
    onChangeLivesInLocation(locations) {
      this.user_registration.userInfo.livesIn = locations;
    },
    onChangeOriginallyFromLocation(locations) {
      this.user_registration.userInfo.originallyFrom = locations;
    },
    createUser(locale) {
      const formData = new FormData();
      this.user_registration.locale = locale;
      buildFormData(formData, this.user_registration, 'user_registration');
      this.errors = {};
      this.isSaving = true;
      if (window.navigator.onLine) {
        this.$api({
          method: 'post',
          url: '/users/sign_up',
          data: formData,
        })
          .then((response) => {
            this.user_registration.userId = response.data.id;
            this.isSaving = false;
            this.goToNextPage('profile-photo');

          }).catch((err) => {
            this.errors.saving_error = i18n.t('error.error_saving_profile', { lng: this.user_registration.locale });
            this.isSaving = false;
          });
      } else {
        this.errors.saving_error = 'There was an error saving your profile, please check your internet connection.';
        this.isSaving = false;
      }
    },
    goToNextPage(nextPage) {
      const previousPage = this.page;
      this.errors = {};
      this.$api({
        method: 'post',
        url: '/registration_data',
        data: { previous_page: previousPage, page: nextPage, user_registration: this.user_registration }
      })
        .then(() => {
          this.page = nextPage;
          history.pushState({ page: nextPage }, '', `/users/sign_up/${nextPage}`)
          window.scrollTo(0, 0);
          this.$nextTick(() => {
            const heading = document.querySelector('h1');
            if (heading) heading.focus();
          });
        }).catch((err) => {
          if (err.status == 440) {
            this.showContinuationModal = true;
          } else if (err instanceof WafInterruptionError) {
            this.errors.fetch_error = "We couldn't verify your browser, please try again.";
          } else {
            this.errors.fetch_error = 'Server error, please try again.';
          }
        });
    },
    goBack(event) {
      switch (event.state.page) {
        case 'secondary-selection':
        case 'school-selection':
          this.toggleAllIncomplete();
          this.goToNextPage('primary-selection');
        default:
          this.page = event.state.page;
          window.scrollTo(0, 0);
          this.$nextTick(() => {
            const heading = document.querySelector('h1');
            if (heading) heading.focus();
          });
      }
    },
  }
}

function buildFormData(formData, data, parentKey) {
  if (data && typeof data === 'object') {
    Object.keys(data).forEach((key) => {
      if (['profile_image', 'original_profile_image'].includes(key)) {
        formData.append(parentKey ? `${parentKey}[${key}]` : key, data[key]);
      }
      if (key == 'school_ids') {
        buildFormData(formData, data[key], parentKey ? `${parentKey}[school_ids_hash]` : key);
      } else if (key == 'livesIn') {
        formData.append(parentKey ? `${parentKey}[livesInName]` : 'livesInName', data[key][0].country);
        formData.append(parentKey ? `${parentKey}[livesInId]` : 'livesInId', data[key][0].country_code);
      } else if (key == 'originallyFrom') {
        formData.append(parentKey ? `${parentKey}[originallyFromName]` : 'originallyFromName', data[key][0].country);
        formData.append(parentKey ? `${parentKey}[originallyFromId]` : 'originallyFromId', data[key][0].country_code);
      } else {
        buildFormData(formData, data[key], parentKey ? `${parentKey}[${key}]` : key);
      }
    });
  } else {
    const value = data == null ? '' : data;
    formData.append(parentKey, value);
  }
}

Alpine.data('OSUController', OSUController)

Alpine.data('verification', verificationRecaptcha);

// Store for avatar editor reference (used by saveUserProfilePhoto)
Alpine.store('osu', {
  avatarEditor: null,
});

Alpine.start();
